import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";
import type { ListeningStepComponentProps } from "./types";
import { theme } from "../../../../theme/theme";
import { usePreloadAudioUrls } from "../../hooks/usePreloadAudioUrls";

export function PromptStep({
  assessment,
  isLoading,
  onStepReadyChange,
}: ListeningStepComponentProps) {
  const promptAudio = assessment?.part?.promptAudio ?? "";
  const [completedPromptAudio, setCompletedPromptAudio] = useState("");
  const [failedPromptAudio, setFailedPromptAudio] = useState("");
  usePreloadAudioUrls([promptAudio]);
  const hasCompletedPromptAudio = completedPromptAudio === promptAudio;
  const isReady = useMemo(
    () =>
      !promptAudio ||
      completedPromptAudio === promptAudio ||
      failedPromptAudio === promptAudio,
    [completedPromptAudio, failedPromptAudio, promptAudio],
  );

  useEffect(() => {
    onStepReadyChange?.(isReady);
  }, [isReady, onStepReadyChange]);

  if (isLoading) {
    return null;
  }

  if (!assessment?.part) {
    return (
      <Typography>No part content available. You can continue.</Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      gap={3}
      sx={{ width: "100%", maxWidth: 1400, mx: "auto" }}
    >
      <Card
        sx={{
          flex: "60%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {assessment.part.promptImage ? (
          <Box
            component="img"
            src={assessment.part.promptImage}
            alt="Listening prompt"
            onError={(event) => {
              const target = event.currentTarget as HTMLImageElement;
              target.style.display = "none";
            }}
            sx={{ height: "100%", width: "100%", objectFit: "contain" }}
          />
        ) : (
          <Typography color="text.secondary">
            Prompt image unavailable. You can continue.
          </Typography>
        )}
      </Card>
      <Card
        sx={{
          flex: "40%",
          display: "flex",
          flexDirection: "column",
          height: { xs: "40vh", md: "50vh" },
          minHeight: 0,
          overflow: "hidden",
        }}
        variant={"filled"}
      >
        <Typography p={3}>
          Listen to the what the students learn here.
        </Typography>
        <CardContent sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {promptAudio && !hasCompletedPromptAudio ? (
            <audio
              controls
              controlsList="nodownload noplaybackrate noremoteplayback"
              preload="auto"
              crossOrigin="anonymous"
              src={promptAudio}
              style={{
                width: "100%",
                backgroundColor: theme.palette.primary.light,
              }}
              onEnded={() => {
                setCompletedPromptAudio(promptAudio);
                setFailedPromptAudio("");
              }}
              onError={() => {
                setFailedPromptAudio(promptAudio);
                setCompletedPromptAudio("");
              }}
            />
          ) : null}
          {!promptAudio ? (
            <Typography color="text.secondary">
              No prompt audio available.
            </Typography>
          ) : null}
          {failedPromptAudio === promptAudio ? (
            <Typography variant="caption" color="text.secondary">
              Prompt audio could not be loaded. You can continue to the next
              step.
            </Typography>
          ) : null}
          {!isReady && failedPromptAudio !== promptAudio ? (
            <Typography variant="caption" color="text.secondary">
              Listen to the full prompt audio to continue.
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Stack>
  );
}
